/**
 * A keyword list, entered as removable tags.
 *
 * Keywords are a list everywhere in this application - an article targets
 * several, a clinic carries defaults, a store overrides them - and every one of
 * those screens previously offered either a single text box or nothing at all.
 * One component so that the rules (how many, how long, what counts as the same
 * keyword twice) are the ones in shared/keywords.ts on every screen, and so the
 * writer learns the interaction once.
 *
 * Why not a `<select multiple>` or a combobox: there is no fixed vocabulary to
 * choose from. A keyword is free text the clinic invents, so the control is a
 * text field that commits what was typed - the pattern every issue tracker and
 * CMS uses for tags, and one people already know.
 *
 * Accessibility is the reason this is 150 lines rather than 30:
 *   - the tags are a real list, so a screen reader announces "list, 3 items"
 *     rather than reading three unlabelled buttons;
 *   - each remove button says which keyword it removes, not just "削除";
 *   - the input is described by the instructions, so the Enter/comma behaviour
 *     is announced on focus rather than discovered;
 *   - additions, rejections and removals are announced through one polite live
 *     region, because a keyword that silently fails to be added looks to a
 *     sighted user like a slow save and to a blind one like nothing at all;
 *   - Backspace on an empty field removes the last tag, which is the one
 *     shortcut people expect from this pattern.
 */
import { useId, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import {
  MAX_KEYWORD_LENGTH,
  MAX_KEYWORDS,
  normalizeKeywords,
  splitKeywordInput,
} from '../../../shared/keywords.ts';

export function KeywordInput({
  id,
  value,
  onChange,
  disabled,
  /**
   * Whether the first keyword is the one the article targets.
   *
   * True on an article, where the list is ordered and the front of it is the
   * primary keyword. False for a clinic's or store's defaults, which are a set:
   * nothing there is written to rank for anything.
   */
  primary = false,
  max = MAX_KEYWORDS,
  placeholder = '例：ダーマペン4 ダウンタイム',
}: {
  id: string;
  value: string[];
  onChange: (keywords: string[]) => void;
  disabled?: boolean;
  /** Marks and reorders the first keyword as the one being targeted. */
  primary?: boolean;
  /** Lower than the shared ceiling where a screen has its own reason to be. */
  max?: number;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  /** Which tag is being edited in place, and what it currently says. */
  const [editing, setEditing] = useState<number | null>(null);
  const [draftEdit, setDraftEdit] = useState('');
  /**
   * What just happened, for the live region.
   *
   * Held in state rather than derived, because the interesting cases are the
   * ones where `value` does NOT change - a duplicate, or a full list - and a
   * message derived from the list could not describe them.
   */
  const [announcement, setAnnouncement] = useState('');
  const helpId = useId();
  const liveId = useId();

  const full = value.length >= max;

  /**
   * Commit whatever is in the field, plus anything pasted with it.
   *
   * Returns nothing and clears the draft only on success: a keyword rejected as
   * a duplicate stays in the field where the writer can see and edit it, rather
   * than vanishing and looking like it was accepted.
   */
  function commit(raw: string): void {
    const candidates = normalizeKeywords(splitKeywordInput(raw));
    if (candidates.length === 0) {
      // Nothing but whitespace or separators. Not an error worth announcing.
      setDraft('');
      return;
    }

    const merged = normalizeKeywords([...value, ...candidates]).slice(0, max);
    const added = merged.length - value.length;

    if (added === 0) {
      setAnnouncement(
        full
          ? `キーワードは${max}件までです。`
          : `「${candidates[0]!}」はすでに追加されています。`,
      );
      return;
    }

    const dropped = candidates.length - added;
    setAnnouncement(
      dropped > 0
        ? `${added}件を追加しました。${dropped}件は重複または上限のため追加していません。`
        : `「${candidates.slice(0, added).join('、')}」を追加しました。`,
    );
    onChange(merged);
    setDraft('');
  }

  function remove(keyword: string): void {
    onChange(value.filter((existing) => existing !== keyword));
    setAnnouncement(`「${keyword}」を削除しました。`);
  }

  /**
   * Make one keyword the primary one by moving it to the front.
   *
   * The first entry is what the article is written to rank for (migration 050),
   * so "which is the primary keyword" is a question about ORDER - and the answer
   * has to be changeable without deleting and retyping the list.
   */
  /**
   * Change one keyword in place, keeping its position.
   *
   * Deleting and retyping was the only way to fix a typo, and on the primary
   * keyword it was worse than a nuisance: the tag lost its place, so the article
   * silently started targeting whatever was next in the list.
   *
   * The same rules as adding - trimmed, deduplicated, length-checked - because a
   * keyword edited into a duplicate is the same problem as one typed into one.
   */
  function rename(at: number, next: string): void {
    const cleaned = normalizeKeywords([next])[0] ?? '';
    if (cleaned === '') {
      // Emptying a tag means removing it, which is what the writer just did.
      onChange(value.filter((_, index) => index !== at));
      setAnnouncement('キーワードを削除しました。');
      return;
    }
    if (value.some((existing, index) => index !== at && existing === cleaned)) {
      setAnnouncement(`「${cleaned}」はすでにあります。`);
      return;
    }
    onChange(value.map((existing, index) => (index === at ? cleaned : existing)));
  }

  function promote(keyword: string): void {
    onChange([keyword, ...value.filter((existing) => existing !== keyword)]);
    setAnnouncement(`「${keyword}」を狙うキーワードにしました。`);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    // Enter and both commas commit. Enter must not submit the surrounding form
    // while there is a keyword waiting to be added - losing it to a save is
    // exactly the frustration this control exists to remove.
    if (event.key === 'Enter' || event.key === ',' || event.key === '、') {
      if (draft.trim() !== '') {
        event.preventDefault();
        commit(draft);
      } else if (event.key !== 'Enter') {
        event.preventDefault();
      }
      return;
    }
    if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      event.preventDefault();
      remove(value[value.length - 1]!);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const text = event.clipboardData.getData('text');
    // A paste with no separator is just typing; let the field handle it so the
    // writer can still edit before committing.
    if (!/[,、\r\n\t]/.test(text)) return;
    event.preventDefault();
    commit(`${draft}${text}`);
  }

  return (
    <div className="keywords">
      {value.length > 0 && (
        <ul className="keywords__list">
          {value.map((keyword, at) => (
            <li
              key={keyword}
              className={`keywords__tag${at === 0 && primary ? ' keywords__tag--primary' : ''}`}
            >
              {/*
                The first tag is the keyword the article is written to rank for.
                Marked rather than merely first, because "first in a wrapping row
                of tags" is not a position anybody reads as meaningful - and
                clickable elsewhere, so changing it costs one press.
              */}
              {primary && at === 0 && (
                <span className="keywords__primary" title="狙うキーワード">
                  狙う
                </span>
              )}
              {/*
                The text itself is editable in place.

                A keyword is one word somebody typed and often mistyped; making
                the fix cost a delete and a retype is why lists like this end up
                with 「ハイフ」 and 「ハイフ 」 side by side. Editing keeps the
                position, which on the first tag is also what the article targets.
              */}
              {editing === at ? (
                <input
                  className="keywords__edit"
                  autoFocus
                  value={draftEdit}
                  maxLength={MAX_KEYWORD_LENGTH}
                  aria-label={`キーワード「${keyword}」を編集`}
                  onChange={(event) => setDraftEdit(event.target.value)}
                  onBlur={() => {
                    rename(at, draftEdit);
                    setEditing(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      rename(at, draftEdit);
                      setEditing(null);
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      // Escape abandons the edit; the keyword is unchanged.
                      setEditing(null);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="keywords__text keywords__edit-open"
                  disabled={disabled}
                  aria-label={
                    primary && at > 0
                      ? `「${keyword}」を編集（長押しで狙うキーワードに）`
                      : `キーワード「${keyword}」を編集`
                  }
                  onClick={() => {
                    setEditing(at);
                    setDraftEdit(keyword);
                  }}
                >
                  {keyword}
                </button>
              )}
              {primary && at > 0 && editing !== at && (
                <button
                  type="button"
                  className="keywords__aim"
                  disabled={disabled}
                  title="狙うキーワードにする"
                  aria-label={`「${keyword}」を狙うキーワードにする`}
                  onClick={() => promote(keyword)}
                >
                  ↑
                </button>
              )}
              <button
                type="button"
                className="keywords__remove"
                disabled={disabled}
                aria-label={`キーワード「${keyword}」を削除`}
                onClick={() => remove(keyword)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        id={id}
        type="text"
        value={draft}
        disabled={disabled || full}
        maxLength={MAX_KEYWORD_LENGTH}
        placeholder={full ? `キーワードは${max}件までです` : placeholder}
        aria-describedby={`${helpId} ${liveId}`}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        // A keyword left in the field when focus moves away is one the writer
        // meant to add; committing it is what they expect the save to have done.
        onBlur={() => draft.trim() !== '' && commit(draft)}
      />

      {/*
        The count is one text node and marked untranslatable on purpose.

        Written as `{value.length}/{max}` React emitted three sibling text nodes
        ("3", "/", "30"), and a browser translating the page rewrote the run as a
        sentence - dropping the first number, so a field holding three keywords
        reported "0 out of 30". The whole counter is built in JS so there is
        nothing to split, and `translate="no"` keeps the numbers out of the
        translator's reach even then.
      */}
      <span className="field__hint" id={helpId}>
        <span translate="no">{`${value.length}/${max}`}</span>
        {` 件・Enter またはカンマで追加・1件${MAX_KEYWORD_LENGTH}文字まで`}
      </span>
      {/*
        One region for every outcome. `role="status"` is polite: a keyword being
        added must not interrupt what a screen reader is already saying.
      */}
      <span className="sr-only" role="status" id={liveId}>
        {announcement}
      </span>
    </div>
  );
}
